import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, Archive, History, Pencil, Search } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  PAGE_SIZE,
  useArchivePromoCode,
  usePromoCodes,
  type CodeKind,
} from './promo-codes.api';
import { isExpired, type PromoCode } from './types';

/**
 * One tab of the promo codes page.
 *
 * Store and customer codes are the same table split on whether the row carries
 * a `userId`, so they are one component with one extra column rather than the
 * two near-identical blocks the original repeated inside its tabs.
 *
 * Each tab owns its own search term. The original had two boxes and one
 * variable: `getUserCodes` sent `businessCodeSearchString`, so typing in the
 * customer-codes box did nothing and the store box quietly filtered both lists.
 * It also hard-coded `page: 0, size: 10` and ignored the `count` it was sent,
 * so only the first ten codes of either kind were ever reachable.
 */

const ANY = 'ANY';

export function CodesTab({
  kind,
  onEdit,
  onViewRedemptions,
}: {
  kind: CodeKind;
  onEdit: (code: PromoCode) => void;
  onViewRedemptions: (code: PromoCode) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [businessId, setBusinessId] = useState(ANY);
  const [page, setPage] = useState(0);
  const [archiving, setArchiving] = useState<PromoCode | null>(null);

  const { data: businesses } = useBusinesses();
  const list = usePromoCodes({
    kind,
    query,
    businessId: businessId === ANY ? null : businessId,
    page,
  });
  const archive = useArchivePromoCode();
  // Rows on screen being replaced — a new search, store or page, or an archive.
  const busy = list.isFetching && !list.isPending;

  const rows = list.data?.coupons ?? [];
  const total = list.data?.count ?? 0;

  // Only the store search endpoint takes a business filter; the user one
  // ignores the field, so offering it there would be a control that does
  // nothing.
  const fields: FilterField[] =
    kind === 'store'
      ? [
          {
            id: 'store',
            label: t('promoCodes.store'),
            value: businessId,
            onChange: (value) => {
              setBusinessId(value);
              setPage(0);
            },
            options: [
              { value: ANY, label: t('promoCodes.allStores') },
              ...(businesses ?? []).map((business) => ({
                value: business.id,
                label: business.name,
              })),
            ],
          },
        ]
      : [];

  const ExpiryCell = ({ code }: { code: PromoCode }) => {
    if (!code.expiresAt) {
      return (
        <Badge variant="secondary">{t('promoCodes.neverExpires')}</Badge>
      );
    }
    const expired = isExpired(code);
    return (
      <div className="space-y-0.5">
        <p className="whitespace-nowrap">{formatDate(code.expiresAt)}</p>
        {expired && (
          <Badge variant="outline">{t('promoCodes.expired')}</Badge>
        )}
      </div>
    );
  };

  const Actions = ({ code }: { code: PromoCode }) => (
    <div className="flex justify-end gap-1">
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t('promoCodes.redemptions')}
        onClick={() => onViewRedemptions(code)}
      >
        <History className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t('promoCodes.editExpiry')}
        onClick={() => onEdit(code)}
      >
        <Pencil className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        className="text-destructive"
        aria-label={t('promoCodes.archive')}
        onClick={() => setArchiving(code)}
      >
        <Archive className="size-4" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-4 pt-4">
      <FilterBar
        busy={list.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void list.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={
                  kind === 'store'
                    ? t('promoCodes.searchStore')
                    : t('promoCodes.searchCustomerCodes')
                }
                aria-label={t('promoCodes.searchLabel')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('promoCodes.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('promoCodes.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('promoCodes.none')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={busy}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('promoCodes.code')}</TableHead>
                  {kind === 'customer' && (
                    <TableHead>{t('promoCodes.customer')}</TableHead>
                  )}
                  <TableHead>{t('promoCodes.store')}</TableHead>
                  <TableHead className="text-right">
                    {t('promoCodes.value')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('promoCodes.minPurchase')}
                  </TableHead>
                  <TableHead>{t('promoCodes.usageLimit')}</TableHead>
                  <TableHead>{t('promoCodes.expires')}</TableHead>
                  <TableHead className="w-32 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((code) => (
                  <TableRow key={code.id} className="group">
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <span className="font-mono text-xs font-medium">
                          {code.code ?? '—'}
                        </span>
                        {code.code && <CopyButton value={code.code} />}
                      </span>
                    </TableCell>
                    {kind === 'customer' && (
                      <TableCell className="truncate">
                        {code.customerName || '—'}
                      </TableCell>
                    )}
                    <TableCell className="truncate">
                      {code.businessName || t('promoCodes.global')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(code.value)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right tabular-nums">
                      {code.minimumAmount > 0
                        ? formatCurrency(code.minimumAmount)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {t(`promoCodes.usage${code.applicationType}`)}
                      <p className="text-muted-foreground">
                        {t(`promoCodes.appliesTo${code.appliesTo}`)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <ExpiryCell code={code} />
                    </TableCell>
                    <TableCell>
                      <Actions code={code} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={busy} />
            <div
              aria-busy={busy}
              className={cn(
                'space-y-2',
                busy && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((code) => (
                <div
                  key={code.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-medium">
                        {code.code ?? '—'}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {kind === 'customer'
                          ? code.customerName || '—'
                          : code.businessName || t('promoCodes.global')}
                      </p>
                    </div>
                    <p className="shrink-0 font-medium tabular-nums">
                      {formatCurrency(code.value)}
                    </p>
                  </div>
                  <div className="text-muted-foreground space-y-0.5 text-xs">
                    <p>
                      {t(`promoCodes.usage${code.applicationType}`)} ·{' '}
                      {t(`promoCodes.appliesTo${code.appliesTo}`)}
                    </p>
                    {code.minimumAmount > 0 && (
                      <p>
                        {t('promoCodes.minPurchase')}:{' '}
                        {formatCurrency(code.minimumAmount)}
                      </p>
                    )}
                  </div>
                  <ExpiryCell code={code} />
                  <Actions code={code} />
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      <AlertDialog
        open={archiving !== null}
        onOpenChange={(next) => {
          if (!next) setArchiving(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('promoCodes.archive')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('promoCodes.archiveConfirm', {
                code: archiving?.code ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                archive.mutate(archiving!.id, {
                  onSuccess: () => {
                    toast.success(t('promoCodes.archived'));
                    setArchiving(null);
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('promoCodes.saveFailed')
                    ),
                })
              }
            >
              {t('promoCodes.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
